import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { CreditCard, X, CheckCircle } from 'lucide-react';
import userService, { type UserData, type Role } from '../../services/user';
import { useAuth } from '../../context/AuthContext';

// The `admin` role's only remaining purpose is gating subscription / billing /
// seat-allocation actions (purchase modules, assign licences, view invoices,
// invite users). All module features are gated by their own licence tier.
// `super_admin` is intentionally NOT assignable through the UI — it is
// granted only by direct database update by the platform developer.
const ASSIGNABLE_ROLES = ['admin'] as const;

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin — Subscription Manager',
};

function formatRoleLabel(name: string) {
  return ROLE_LABELS[name] ?? name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function SubscriptionManagers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserData[]>([]);
  const [userRoles, setUserRoles] = useState<Record<number, Role[]>>({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const usersResponse = await userService.getUsers();
      if (usersResponse.success && usersResponse.data) {
        const fetchedUsers = usersResponse.data.filter(
          u => u.organisation_id === currentUser?.organisation_id
        );
        setUsers(fetchedUsers);
        const rolesMap: Record<number, Role[]> = {};
        for (const user of fetchedUsers) {
          const res = await userService.getUserRoles(user.id);
          if (res.success) rolesMap[user.id] = res.data;
        }
        setUserRoles(rolesMap);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.organisation_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshUserRoles = async (userId: number) => {
    const res = await userService.getUserRoles(userId);
    if (res.success) {
      setUserRoles(prev => ({ ...prev, [userId]: res.data }));
    }
  };

  const handleAssign = async (userId: number, roleName: string) => {
    try {
      setProcessing(true);
      const response = await userService.assignRole(userId, roleName);
      if (response.success) await refreshUserRoles(userId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to assign';
      alert(message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRemove = async (userId: number, roleName: string) => {
    if (!confirm(`Remove '${formatRoleLabel(roleName)}' from this user?`)) return;
    try {
      setProcessing(true);
      const response = await userService.removeRole(userId, roleName);
      if (response.success) await refreshUserRoles(userId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to remove';
      alert(message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-center text-neutral-600">Loading subscription managers...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <CreditCard className="h-5 w-5 text-purple-600" />
          </div>
          Organisation Admins (Subscription Managers)
        </CardTitle>
        <p className="mt-2 text-sm text-neutral-500">
          Designate which users are admins for this organisation. Admins can invite users, purchase
          modules, assign licence seats, and manage Xero billing. All other module features are
          controlled by individual licence seats, not by this designation.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {users.length === 0 ? (
            <p className="text-center text-neutral-600 py-4">No users found in your organization</p>
          ) : (
            users.map((user) => {
              const currentRoles = (userRoles[user.id] ?? [])
                .filter((r, i, arr) => arr.findIndex(x => x.name === r.name) === i)
                .filter(r => ASSIGNABLE_ROLES.includes(r.name as typeof ASSIGNABLE_ROLES[number]));
              const currentRoleNames = currentRoles.map(r => r.name);
              const assignable = ASSIGNABLE_ROLES.filter(r => !currentRoleNames.includes(r));

              return (
                <div
                  key={user.id}
                  className="border border-neutral-200 rounded-lg p-4 hover:border-purple-200 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-neutral-900">{user.username}</p>
                      <p className="text-sm text-neutral-500">{user.email}</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {currentRoles.map((role) => (
                        <span
                          key={role.name}
                          className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm"
                        >
                          <CheckCircle className="h-3 w-3" />
                          {formatRoleLabel(role.name)}
                          <button
                            onClick={() => handleRemove(user.id, role.name)}
                            disabled={processing}
                            className="ml-1 hover:text-purple-900 disabled:opacity-50"
                            title="Remove"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}

                      {assignable.map((role) => (
                        <button
                          key={role}
                          onClick={() => handleAssign(user.id, role)}
                          disabled={processing}
                          className="text-xs px-3 py-1 rounded-full border border-purple-300 text-purple-600 hover:bg-purple-50 disabled:opacity-50 transition-colors"
                        >
                          + {formatRoleLabel(role)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default SubscriptionManagers;
